import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map routes to friendly page names
const PAGE_TITLES = {
  '/': 'Landing Page',
  '/login': 'Login',
  '/register': 'Registration',
  '/customer': 'Customer Dashboard',
  '/customer/request': 'Requesting Service',
  '/customer/history': 'Service History',
  '/customer/tracking': 'Tracking Service',
  '/mechanic': 'Provider Dashboard',
  '/mechanic/job': 'Job Details',
  '/marketplace': 'Marketplace',
  '/marketplace/sell': 'Creating Listing',
  '/marketplace/my-listings': 'My Listings',
  '/marketplace/product': 'Viewing Product',
  '/cart': 'Shopping Cart',
  '/settings': 'Account Settings',
  '/profile/setup': 'Profile Setup',
  '/employee/settings': 'Employee Settings',
  '/admin': 'Admin Dashboard',
  '/admin/analytics': 'Analytics Dashboard',
  '/admin/design': 'Design Tools',
  '/owner': 'Owner Command Center',
  '/pending-approval': 'Awaiting Approval',
  '/apply': 'Provider Application'
};

export function getPageTitle(pathname) {
  // Check for exact match first
  if (PAGE_TITLES[pathname]) {
    return PAGE_TITLES[pathname];
  }
  
  // Check for partial matches (for dynamic routes)
  for (const [route, title] of Object.entries(PAGE_TITLES)) {
    if (pathname.startsWith(route) && route !== '/') {
      return title;
    }
  }
  
  return 'Browsing';
}

export function useActivityTracker() {
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const { isConnected, sendActivity: sendWsActivity } = useWebSocket();
  const lastSentRef = useRef({ path: '', time: 0 });
  
  useEffect(() => {
    if (!user) return;
    
    const sendActivity = async () => {
      const now = Date.now();
      const currentPath = location.pathname;
      const pageTitle = getPageTitle(currentPath);
      
      // Debounce: don't send if same path within 5 seconds
      if (
        lastSentRef.current.path === currentPath &&
        now - lastSentRef.current.time < 5000
      ) {
        return;
      }
      
      lastSentRef.current = { path: currentPath, time: now };
      
      // Prefer WebSocket if connected
      if (isConnected) {
        sendWsActivity(currentPath, pageTitle);
        return;
      }
      
      // Fallback to HTTP POST
      try {
        await axios.post(
          `${API}/user/activity`,
          {
            current_page: currentPath,
            page_title: pageTitle
          },
          {
            headers: getAuthHeaders(),
            withCredentials: true
          }
        );
      } catch (error) {
        // Silent fail - activity tracking is non-critical
        console.debug('Activity tracking failed:', error.message);
      }
    };
    
    // Send activity on page change
    sendActivity();
    
    // Also send activity every 15 seconds to keep session alive
    const interval = setInterval(sendActivity, 15000);
    
    return () => clearInterval(interval);
  }, [location.pathname, user, getAuthHeaders, isConnected, sendWsActivity]);
}

export default useActivityTracker;
